import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { authedDelete, authedPatch, authedPost } from '@/lib/api';
import { BudgetCategory } from '@/types/budgets';

export const useBudgetCategories = (budgetId?: string) => {
  return useQuery({
    queryKey: ['budgetCategories', budgetId],
    queryFn: async () => {
      if (!budgetId) return [];

      const { data, error } = await supabase
        .from('budget_categories')
        .select('*')
        .eq('budget_id', budgetId)
        .order('sort_order');

      if (error) throw error;
      return data as BudgetCategory[];
    },
    enabled: !!budgetId,
  });
};

// Writes go through the Hono backend (Pattern B, /api/v1/budgets — see
// apps/api/src/routes/accounting/budgets.ts); the read above stays direct for
// now. The API pins every category to its budget and the budget to the org.
export const categoriesPath = (organizationId: string, budgetId: string) =>
  `/api/v1/budgets/orgs/${organizationId}/budgets/${budgetId}/categories`;

export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      group_id: string;
      budget_id: string;
      organization_id: string;
      name: string;
      gl_account_id?: string | null;
      sort_order?: number;
      rollover_overspending?: boolean;
      default_budgeted?: number | null;
      total_budgeted?: number;
    }) => {
      // organization_id / budget_id travel in the URL, never the body.
      const { budget_id, organization_id, ...body } = data;
      return authedPost<BudgetCategory>(categoriesPath(organization_id, budget_id), body);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['budgetCategories', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetMonthView', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetMultiMonthView', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetTotalView', result.budget_id] });
      // No success toast — batch saves handle their own feedback
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

/** The org comes from OrganizationContext; `budgetId` scopes the API path. */
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, budgetId, data }: {
      id: string;
      budgetId: string;
      data: Partial<Pick<BudgetCategory, 'name' | 'sort_order' | 'is_hidden' | 'gl_account_id' | 'group_id' | 'rollover_overspending' | 'notes' | 'default_budgeted'>>;
    }) => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      return authedPatch<BudgetCategory>(`${categoriesPath(selectedOrganization.id, budgetId)}/${id}`, data);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['budgetCategories', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetMonthView', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetMultiMonthView', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetTotalView', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetToBudget', result.budget_id] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, budgetId }: { id: string; budgetId: string }) => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      await authedDelete<{ deleted: number }>(`${categoriesPath(selectedOrganization.id, budgetId)}/${id}`);
      return { budgetId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['budgetCategories', result.budgetId] });
      queryClient.invalidateQueries({ queryKey: ['budgetMonthView', result.budgetId] });
      queryClient.invalidateQueries({ queryKey: ['budgetMultiMonthView', result.budgetId] });
      queryClient.invalidateQueries({ queryKey: ['budgetTotalView', result.budgetId] });
      // No success toast — batch saves handle their own feedback
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
